<template>
  <div class="collection">
    <div class="collection-layout">
      <aside v-show="!menusCompact" class="card-viewer-rail">
        <CardViewer
          :card="selectedCard"
          :width="260"
          :showInfo="true"
          :foil-choices="selectedFoilChoices"
          @update:foil="onPreferredFoil"
        />
        <p v-if="viewerMissing && selectedCard" class="missing-note">
          {{ t('collection.slotEmpty') }}
        </p>
      </aside>

      <div class="collection-main">
        <header class="page-header">
          <div>
            <p class="kicker">{{ t('collection.kicker') }}</p>
            <h1>{{ t('collection.title') }}</h1>
            <p class="support">
              {{ t('collection.lead') }}
              <router-link to="/packs" class="text-link">
                {{ t('collection.openPacks') }}
              </router-link>
            </p>
          </div>
          <div class="header-meta">
            <p class="progress" :aria-label="progressLabel">
              <span class="progress-n">{{ ownedUnique }}</span>
              <span class="progress-sep">/</span>
              <span>{{ total }}</span>
            </p>
            <button
              v-if="menusCompact"
              type="button"
              class="search-icon-btn"
              :class="{ active: albumFiltersDirty }"
              :aria-label="t('collection.searchFilters')"
              :aria-expanded="filterModalOpen"
              @click="openFilterModal"
            >
              <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <circle cx="10.5" cy="10.5" r="6.5" />
                <path d="M16 16l5 5" />
              </svg>
              <span v-if="albumFiltersDirty" class="search-dot" />
            </button>
          </div>
        </header>

        <section class="md-panel">
          <div v-if="!menusCompact" class="filters">
            <input
              id="album-search"
              v-model="searchQuery"
              type="text"
              class="md-field"
              :placeholder="t('collection.search')"
              :aria-label="t('collection.search')"
            >
            <fieldset class="filter-row">
              <legend class="visually-hidden">{{ t('collection.filter') }}</legend>
              <button
                v-for="opt in FILTERS"
                :key="opt"
                type="button"
                class="md-btn-tonal"
                :class="{ selected: filter === opt }"
                @click="filter = opt"
              >
                {{ t(`collection.${opt}`) }}
              </button>
            </fieldset>
            <fieldset class="filter-row">
              <legend class="visually-hidden">{{ t('collection.setFilter') }}</legend>
              <button
                type="button"
                class="md-btn-tonal"
                :class="{ selected: setFilter === 'all' }"
                @click="setFilter = 'all'"
              >
                {{ t('collection.setAll') }}
              </button>
              <button
                v-for="prefix in ALBUM_PREFIXES"
                :key="prefix"
                type="button"
                class="md-btn-tonal"
                :class="{ selected: setFilter === prefix }"
                @click="setFilter = prefix"
              >
                {{ prefix }}
              </button>
            </fieldset>
          </div>

          <div v-if="slots.length === 0" class="empty-state">
            <p>{{ t('collection.emptyFilter') }}</p>
          </div>
          <div
            v-else
            class="album-stage"
            :aria-busy="!albumReady"
          >
            <div
              v-if="!albumReady"
              class="album-loader"
            >
              <span class="album-spinner" aria-hidden="true" />
              <p>{{ t('collection.loading') }}</p>
            </div>
            <div v-else class="album-grid">
              <button
                v-for="slot in slots"
                :key="slot.card.id"
                type="button"
                class="album-slot"
                :class="{
                  owned: slot.owned,
                  missing: !slot.owned,
                  selected: peekId === slot.card.id,
                }"
                :aria-label="slotLabel(slot)"
                :title="slotLabel(slot)"
                @mouseenter="select(slot)"
                @focus="select(slot)"
                @click="select(slot)"
                @contextmenu.prevent="onSlotContext(slot, $event)"
              >
                <CardArt
                  v-if="slot.owned"
                  class="slot-art"
                  :card-id="slot.card.id"
                  :foil="collection.displayFoil(slot.card.id)"
                  alt=""
                  intensity="thumb"
                />
                <span v-else class="slot-code">{{ slot.code }}</span>
                <span v-if="slot.owned" class="slot-caption">
                  {{ slot.code }}
                </span>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>

    <CardPreviewSheet
      :visible="menusCompact && !!selectedCard"
      :wide="menusShort"
      @close="selectedCard = null"
    >
      <div
        class="album-sheet-viewer"
        :class="{ short: menusShort }"
      >
        <CardViewer
          :card="selectedCard"
          :width="sheetViewerWidth"
          :showInfo="true"
          :foil-choices="selectedFoilChoices"
          :layout="menusShort ? 'row' : 'column'"
          :fluid="menusShort"
          @update:foil="onPreferredFoil"
        />
        <p v-if="viewerMissing && selectedCard" class="missing-note">
          {{ t('collection.slotEmpty') }}
        </p>
      </div>
    </CardPreviewSheet>
    <FoilPickPopover
      v-if="foilPick"
      :rect="foilPick.rect"
      :choices="foilPick.choices"
      :model-value="foilPick.value"
      :label="t('collection.preferredFoil')"
      @update:model-value="onFoilPicked"
      @close="foilPick = null"
    />

    <Teleport to=".app-shell">
      <div
        v-if="filterModalOpen"
        class="filter-modal"
        :class="{ short: menusShort }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="filterModalTitleId"
        @pointerup.self="closeFilterModal"
      >
        <div
          ref="filterModalPanelEl"
          class="filter-modal-panel"
          tabindex="-1"
          @pointerdown.stop
          @pointerup.stop
        >
          <div class="filter-modal-head">
            <h3 :id="filterModalTitleId">
              {{ t('collection.searchFilters') }}
            </h3>
            <button
              type="button"
              class="filter-modal-close"
              :aria-label="t('mobile.close')"
              @click="closeFilterModal"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <div class="filter-modal-body">
            <div class="search-field">
              <input
                :id="filterModalSearchId"
                ref="filterModalSearchEl"
                v-model="draftSearchQuery"
                type="text"
                class="md-field"
                :readonly="searchUntilTap"
                :placeholder="t('collection.search')"
                :aria-label="t('collection.search')"
                @pointerdown="onFilterSearchPointerDown"
              >
              <button
                v-if="draftSearchQuery"
                type="button"
                class="search-clear"
                :aria-label="t('collection.clearSearch')"
                @click="clearDraftSearch"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <fieldset class="filter-row">
              <legend class="visually-hidden">
                {{ t('collection.filter') }}
              </legend>
              <button
                v-for="opt in FILTERS"
                :key="opt"
                type="button"
                class="md-btn-tonal"
                :class="{ selected: draftFilter === opt }"
                @click="draftFilter = opt"
              >
                {{ t(`collection.${opt}`) }}
              </button>
            </fieldset>
            <fieldset class="filter-row">
              <legend class="visually-hidden">
                {{ t('collection.setFilter') }}
              </legend>
              <button
                type="button"
                class="md-btn-tonal"
                :class="{ selected: draftSetFilter === 'all' }"
                @click="draftSetFilter = 'all'"
              >
                {{ t('collection.setAll') }}
              </button>
              <button
                v-for="prefix in ALBUM_PREFIXES"
                :key="prefix"
                type="button"
                class="md-btn-tonal"
                :class="{ selected: draftSetFilter === prefix }"
                @click="draftSetFilter = prefix"
              >
                {{ prefix }}
              </button>
            </fieldset>
          </div>
          <div class="filter-modal-actions">
            <button
              type="button"
              class="md-btn-text"
              :disabled="!draftFiltersDirty"
              @click="resetDraftFilters"
            >
              {{ t('collection.resetFilters') }}
            </button>
            <button
              type="button"
              class="md-btn-filled"
              @click="applyFilterModal"
            >
              {{ t('collection.applyFilters') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { asCardFoil, type CardFoilId } from '@shared/index'
import { useCollectionStore } from '../stores/collection'
import { getLocalCardImageUrl } from '../utils/cardImages'
import { loadCardImages, peekCardImage } from '../utils/cardImageCache'
import {
  allCatalogCards,
  albumSetRank,
  ALBUM_PREFIXES,
  lobSetCode,
  lobSetNumber,
  setCodeNumber,
  setPrintingCode,
  type AlbumSetPrefix,
  type CatalogCard,
} from '../game/packs'
import CardViewer from '../components/CardViewer.vue'
import type { CardViewerData } from '../components/CardViewer.vue'
import CardArt from '../components/CardArt.vue'
import CardPreviewSheet from '../components/CardPreviewSheet.vue'
import FoilPickPopover from '../components/FoilPickPopover.vue'
import type { FoilPickRect } from '../components/FoilPickPopover.vue'
import { localizedName } from '../i18n/localizeCard'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'

type AlbumFilter = 'all' | 'owned' | 'missing'
type AlbumSetFilter = 'all' | AlbumSetPrefix

interface AlbumSlot {
  card: CatalogCard
  code: string
  number: number
  setRank: number
  owned: boolean
}

const FILTERS: AlbumFilter[] = ['all', 'owned', 'missing']

const { t } = useI18n()
const { menusCompact, menusShort } = useCompactLayout()
const collection = useCollectionStore()
const sheetViewerWidth = computed(() => (menusShort.value ? 128 : 260))
const route = useRoute()

function setFilterFromQuery(): AlbumSetFilter {
  const raw = String(route.query.set ?? '').toUpperCase()
  return (ALBUM_PREFIXES as readonly string[]).includes(raw)
    ? (raw as AlbumSetPrefix)
    : 'all'
}

const searchQuery = ref('')
const filter = ref<AlbumFilter>('all')
const setFilter = ref<AlbumSetFilter>(setFilterFromQuery())
const filterModalOpen = ref(false)
const filterModalTitleId = 'album-filter-modal-title'
const filterModalSearchId = 'album-search-modal'
const filterModalPanelEl = ref<HTMLElement | null>(null)
const filterModalSearchEl = ref<HTMLInputElement | null>(null)
const searchUntilTap = ref(true)
const draftSearchQuery = ref('')
const draftFilter = ref<AlbumFilter>('all')
const draftSetFilter = ref<AlbumSetFilter>('all')
const albumReady = ref(false)
const selectedCard = ref<CardViewerData | null>(null)
const peekId = ref<string | null>(null)
const viewerMissing = ref(false)
let preloadGen = 0
const PRELOAD_MS = 8000
const foilPick = ref<{
  value: CardFoilId
  choices: CardFoilId[]
  rect: FoilPickRect
} | null>(null)

const album = computed((): AlbumSlot[] => {
  return allCatalogCards()
    .map((card) => ({
      card,
      code: lobSetCode(card.id),
      number: lobSetNumber(card.id),
      setRank: albumSetRank(card.id),
      owned: collection.seen(card.id),
    }))
    .sort(
      (a, b) =>
        a.setRank - b.setRank
        || a.number - b.number
        || a.code.localeCompare(b.code),
    )
})

const setAlbum = computed(() => {
  if (setFilter.value === 'all') return album.value
  const prefix = setFilter.value
  return allCatalogCards()
    .flatMap((card) => {
      const code = setPrintingCode(card.id, prefix)
      if (!code) return []
      return [{
        card,
        code,
        number: setCodeNumber(code),
        setRank: albumSetRank(card.id),
        owned: collection.seen(card.id),
      }]
    })
    .sort((a, b) => a.number - b.number || a.code.localeCompare(b.code))
})

const total = computed(() => setAlbum.value.length)
const ownedUnique = computed(
  () => setAlbum.value.filter((slot) => slot.owned).length,
)

const progressLabel = computed(() =>
  t('collection.progress', { owned: ownedUnique.value, total: total.value }),
)

const albumFiltersDirty = computed(() =>
  Boolean(
    searchQuery.value.trim()
    || filter.value !== 'all'
    || setFilter.value !== 'all',
  ),
)

const draftFiltersDirty = computed(() =>
  Boolean(
    draftSearchQuery.value.trim()
    || draftFilter.value !== 'all'
    || draftSetFilter.value !== 'all',
  ),
)

function copyLiveFiltersToDraft(): void {
  draftSearchQuery.value = searchQuery.value
  draftFilter.value = filter.value
  draftSetFilter.value = setFilter.value
}

function applyDraftFilters(): void {
  searchQuery.value = draftSearchQuery.value
  filter.value = draftFilter.value
  setFilter.value = draftSetFilter.value
}

function resetDraftFilters(): void {
  draftSearchQuery.value = ''
  draftFilter.value = 'all'
  draftSetFilter.value = 'all'
}

/**
 * Opens the album filter sheet. Focus the panel, not the search field —
 * focusing the input pops the mobile keyboard before the player asked.
 */
function openFilterModal(): void {
  copyLiveFiltersToDraft()
  filterModalOpen.value = true
  void nextTick(() => {
    filterModalPanelEl.value?.focus()
  })
}

function closeFilterModal(): void {
  filterModalOpen.value = false
}

function applyFilterModal(): void {
  applyDraftFilters()
  filterModalOpen.value = false
}

function onFilterSearchPointerDown(event: PointerEvent): void {
  if (!searchUntilTap.value) return
  searchUntilTap.value = false
  const el = event.currentTarget as HTMLInputElement
  void nextTick(() => el.focus())
}

/** Empty the filter-sheet search without popping the keyboard first. */
function clearDraftSearch(): void {
  draftSearchQuery.value = ''
  if (searchUntilTap.value) return
  void nextTick(() => filterModalSearchEl.value?.focus())
}

function onFilterModalKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && filterModalOpen.value) {
    closeFilterModal()
  }
}

const slots = computed(() => {
  const q = searchQuery.value.toLowerCase().trim()
  const mode = filter.value
  return setAlbum.value.filter((slot) => {
    if (mode === 'owned' && !slot.owned) return false
    if (mode === 'missing' && slot.owned) return false
    if (!q) return true
    const loc = localizedName(slot.card.id, slot.card.name).toLowerCase()
    const en = slot.card.name.toLowerCase()
    if (loc.includes(q) || en.includes(q) || slot.code.toLowerCase().includes(q)) {
      return true
    }
    return ALBUM_PREFIXES.some((prefix) => {
      const code = setPrintingCode(slot.card.id, prefix)
      return code.toLowerCase().includes(q)
    })
  })
})

const ownedImageUrls = computed(() =>
  album.value
    .filter((slot) => slot.owned)
    .map((slot) => getLocalCardImageUrl(slot.card.id)),
)

/**
 * Decode owned art before mounting the grid so foil and picture appear together.
 */
async function preloadOwnedArt(urls: string[], gen: number): Promise<void> {
  const missing = urls.filter((url) => !peekCardImage(url))
  if (missing.length === 0) {
    albumReady.value = true
    return
  }
  albumReady.value = false
  await Promise.race([
    loadCardImages(missing),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, PRELOAD_MS)
    }),
  ])
  if (gen !== preloadGen) return
  albumReady.value = true
}

watch(
  ownedImageUrls,
  (urls) => {
    const gen = ++preloadGen
    void preloadOwnedArt(urls, gen)
  },
  { immediate: true },
)

watch(menusCompact, (compact) => {
  if (!compact) filterModalOpen.value = false
})

watch(() => route.query.set, () => {
  setFilter.value = setFilterFromQuery()
})

watch(filterModalOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) searchUntilTap.value = true
})

onMounted(() => {
  window.addEventListener('keydown', onFilterModalKey)
})

onUnmounted(() => {
  preloadGen += 1
  window.removeEventListener('keydown', onFilterModalKey)
  document.body.style.overflow = ''
})

/**
 * Maps a catalog card into the preview rail.
 */
function toViewer(
  card: CatalogCard,
  foil?: CardFoilId | null,
): CardViewerData {
  return {
    id: card.id,
    name: card.name,
    type: card.type,
    level: card.level,
    typeline: card.typeline,
    race: card.race,
    attribute: card.attribute,
    atk: card.atk,
    def: card.def,
    desc: card.desc,
    foil: foil === undefined ? collection.displayFoil(card.id) : foil,
  }
}

function select(slot: AlbumSlot): void {
  peekId.value = slot.card.id
  viewerMissing.value = !slot.owned
  selectedCard.value = toViewer(
    slot.card,
    collection.displayFoil(slot.card.id),
  )
}

const selectedFoilChoices = computed((): CardFoilId[] => {
  if (!selectedCard.value) return []
  return collection.ownedFoils(selectedCard.value.id)
})

function onPreferredFoil(foil: CardFoilId): void {
  const card = selectedCard.value
  if (!card) return
  collection.setPreferredFoil(card.id, foil)
  selectedCard.value = { ...card, foil: asCardFoil(foil) }
}

/**
 * Right-click an owned album slot to pick the preferred rarity on the card.
 */
function onSlotContext(slot: AlbumSlot, event: MouseEvent): void {
  if (!slot.owned) return
  select(slot)
  const choices = collection.ownedFoils(slot.card.id)
  if (choices.length < 2) return
  const el = event.currentTarget as HTMLElement
  foilPick.value = {
    value: collection.displayFoil(slot.card.id),
    choices,
    rect: el.getBoundingClientRect(),
  }
}

function onFoilPicked(foil: CardFoilId): void {
  foilPick.value = null
  onPreferredFoil(foil)
}

function slotLabel(slot: AlbumSlot): string {
  const name = localizedName(slot.card.id, slot.card.name)
  const state = slot.owned
    ? t('collection.owned')
    : t('collection.missing')
  return `${slot.code} · ${name} · ${state}`
}
</script>

<style scoped>
.collection {
  min-height: calc(100dvh - var(--app-nav-offset, 64px));
  padding: 24px 16px 48px;
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

.collection-layout {
  --viewer-rail-width: 280px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  max-width: 1600px;
  margin: 0 auto;
}

.card-viewer-rail {
  flex: 0 0 var(--viewer-rail-width);
  width: var(--viewer-rail-width);
  position: sticky;
  top: 1rem;
  align-self: flex-start;
  z-index: 20;
  max-height: calc(100vh - 5rem);
  overflow-y: auto;
}

.missing-note {
  margin: 8px 0 0;
  color: var(--md-sys-color-on-surface-variant, #c6c5d0);
  font-size: 13px;
  line-height: 18px;
}

.collection-main {
  flex: 1;
  min-width: 0;
}

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.support {
  margin: 8px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.text-link {
  color: var(--md-sys-color-primary);
  font-weight: 500;
  text-decoration: none;
}

.text-link:hover {
  color: var(--md-sys-color-on-surface);
}

.progress {
  margin: 0;
  white-space: nowrap;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 28px;
  line-height: 36px;
  font-weight: 400;
}

.progress-n {
  color: var(--md-sys-color-primary);
}

.progress-sep {
  margin: 0 4px;
  opacity: 0.6;
}

.md-panel {
  padding: 20px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.md-field {
  min-height: 40px;
  padding: 8px 16px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
}

.md-field:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.md-btn-tonal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  cursor: pointer;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
}

.md-btn-tonal.selected {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
}

.md-btn-filled,
.md-btn-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  cursor: pointer;
  white-space: nowrap;
}

.md-btn-filled {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.md-btn-filled:hover:not(:disabled) {
  filter: brightness(1.08);
}

.md-btn-text {
  background: transparent;
  color: var(--md-sys-color-primary);
}

.md-btn-text:hover:not(:disabled) {
  background: var(--md-state-layer);
}

.md-btn-filled:disabled,
.md-btn-text:disabled {
  opacity: 0.38;
  cursor: not-allowed;
  filter: none;
}

.search-field {
  position: relative;
}

.search-field .md-field {
  width: 100%;
  padding-right: 40px;
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant, #cac4d0);
  transform: translateY(-50%);
  cursor: pointer;
}

.search-clear:hover {
  background: var(--md-sys-color-surface-container-highest, #2b2930);
  color: var(--md-sys-color-on-surface);
}

.search-clear:focus-visible {
  outline: 2px solid var(--md-sys-color-primary, #e8c547);
  outline-offset: 1px;
}

.search-icon-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 0;
  border-radius: 20px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  cursor: pointer;
}

.search-icon-btn.active {
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
}

.search-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--md-sys-color-primary);
}

.filter-modal {
  position: fixed;
  inset: 0;
  z-index: 250;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 max(8px, env(safe-area-inset-right))
    max(8px, env(safe-area-inset-bottom))
    max(8px, env(safe-area-inset-left));
  background: var(--md-scrim, rgba(4, 6, 16, 0.82));
}

.filter-modal.short {
  align-items: center;
  padding: max(8px, env(safe-area-inset-top))
    max(12px, env(safe-area-inset-right))
    max(8px, env(safe-area-inset-bottom))
    max(12px, env(safe-area-inset-left));
}

.filter-modal-panel {
  display: flex;
  flex-direction: column;
  width: min(480px, 100%);
  max-height: min(88dvh, 720px);
  overflow: hidden;
  padding: 12px 12px 0;
  border-radius: 16px 16px 8px 8px;
  background: var(--md-sys-color-surface-container, #1f1f23);
  color: var(--md-sys-color-on-surface, #e5e1e6);
  box-shadow: var(--md-elevation-1);
}

.filter-modal-panel:focus {
  outline: none;
}

.filter-modal-panel:focus-visible {
  outline: 2px solid var(--md-sys-color-primary, #e8c547);
  outline-offset: 2px;
}

.filter-modal.short .filter-modal-panel {
  width: min(840px, 100%);
  max-height: calc(100dvh - 16px);
  border-radius: 12px;
}

.filter-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  margin-bottom: 8px;
}

.filter-modal-head h3 {
  margin: 0;
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
}

.filter-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 18px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  cursor: pointer;
}

.filter-modal-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 8px;
}

.filter-modal-body > .search-field {
  width: 100%;
}

.filter-modal-body > .search-field .md-field[readonly] {
  opacity: 1;
  cursor: text;
}

.filter-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding: 8px 0 max(12px, env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--md-sys-color-outline-variant);
}

.empty-state {
  padding: 32px 8px;
  text-align: center;
  color: var(--md-sys-color-on-surface-variant);
}

.album-stage {
  position: relative;
  min-height: 220px;
}

.album-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 220px;
  padding: 32px 8px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.album-loader p {
  margin: 0;
}

.album-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid color-mix(
    in srgb,
    var(--md-sys-color-outline) 45%,
    transparent
  );
  border-top-color: var(--md-sys-color-primary);
  border-radius: 50%;
  animation: ygo-album-spin 0.7s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .album-spinner {
    animation: none;
    opacity: 0.55;
  }
}

.album-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(92px, 1fr));
  gap: 10px;
}

.album-slot {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 421 / 614;
  padding: 0;
  margin: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: var(--md-sys-color-surface-container-high);
  color: inherit;
  font: inherit;
}

.album-slot.owned {
  overflow: hidden;
}

.album-slot:hover,
.album-slot:focus-visible,
.album-slot.selected {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
  z-index: 2;
}

.slot-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 6px;
}

.slot-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  padding: 3px 4px;
  background: color-mix(in srgb, #000 62%, transparent);
  color: #fff;
  font-size: 10px;
  line-height: 1.2;
  letter-spacing: 0.2px;
  text-align: center;
}

.album-slot.missing {
  border: 1px dashed var(--md-sys-color-outline);
  background:
    repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 6px,
      color-mix(in srgb, var(--md-sys-color-outline) 12%, transparent) 6px,
      color-mix(in srgb, var(--md-sys-color-outline) 12%, transparent) 7px
    );
}

.slot-code {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 6px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.4px;
  text-align: center;
}

@media (max-width: 1024px) {
  .collection-layout {
    flex-direction: column;
    align-items: stretch;
  }

  .card-viewer-rail {
    position: static;
    width: 100%;
    display: flex;
    justify-content: center;
  }
}

@media (max-width: 720px) {
  h1 {
    font-size: 28px;
    line-height: 36px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .progress {
    font-size: 22px;
    line-height: 28px;
  }

  .card-viewer-rail {
    display: none;
  }

  .album-grid {
    grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
    gap: 8px;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .collection {
    padding: 10px 16px 24px;
  }

  h1 {
    font-size: 22px;
    line-height: 28px;
  }

  .page-header {
    margin-bottom: 10px;
    flex-direction: row;
    align-items: flex-end;
  }

  .support {
    display: none;
  }

  .album-grid {
    grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
    gap: 6px;
  }

  .md-panel {
    padding: 12px;
  }
}

.album-sheet-viewer {
  width: 100%;
}

.album-sheet-viewer.short {
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.album-sheet-viewer.short :deep(.card-viewer) {
  height: 100%;
}
</style>

<style>
@keyframes ygo-album-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
